import { useTranslation } from "react-i18next";
import { useState } from "react";
import DOMPurify from "dompurify";
import { Modal } from "../Modal";
import { formatNotificaitonTime } from "@/utils/notifications.util";
import { Skeleton } from "../ui/skeleton";
import { format } from "date-fns";
import { AnnouncementFallbackImg } from "@/assets/images";
import { useGetAnnouncementByIdQuery } from "@/services/api.announcements";
import { AnnouncementIconSvg } from "@/assets/svgs";
import { Clock } from "lucide-react";

interface IProps {
  announcementId: number;
  onClose: () => void;
  isFromCompany?: boolean;
}

const AnnouncementModal = ({ announcementId, onClose }: IProps) => {
  const { t } = useTranslation(["common", "nav", "company"]);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const { data: mySpaceAnnouncement, isLoading: isDataLoading } =
    useGetAnnouncementByIdQuery(Number(announcementId), {
      skip: !announcementId,
      refetchOnMountOrArgChange: true,
    });

  const announcement = mySpaceAnnouncement?.data?.announcementData;
  const coverImage = announcement?.coverImageUrl || AnnouncementFallbackImg;

  const sanitizeRichText = (html: string): string => {
    return DOMPurify.sanitize(html)
      .replace(/&nbsp;/g, " ")
      .replace(/\u00A0/g, " ");
  };

  return (
    <Modal
      size="2xl"
      onOpenChange={onClose}
      open={true}
      onCloseClick={onClose}
      styles={{
        dialogBody: "p-0! max-h-[90vh]!",
        dialogContent: "gap-0! p-0!  md:max-w-2xl! overflow-hidden",
        dialogHeader: "p-2! text-start!",
        dialogTitle: "text-md",
      }}
      dialogData={{
        title: announcement?.type ?? t("common:inbox.announcement"),
        body: (
          <div className="overflow-hidden flex flex-col max-h-[70vh] md:max-h-[80vh] scrollbar-thin shadow-xs border">
            {/* Cover Image - Fixed, no scroll */}
            <div className="relative w-full h-52 sm:h-60 md:h-80 shrink-0 bg-gray-100 overflow-hidden">
              {isDataLoading ? (
                <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
              ) : (
                <>
                  {isImageLoading && (
                    <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
                  )}
                  <img
                    src={coverImage}
                    alt={announcement?.subject || "Announcement"}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      isImageLoading ? "opacity-0" : "opacity-100"
                    }`}
                    onLoad={() => setIsImageLoading(false)}
                    onError={() => setIsImageLoading(false)}
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-linear-to-b from-black/10 via-transparent to-black/70" />
                </>
              )}

              {/* Subject Over Image - Fixed */}
              <div className="absolute bottom-0 start-0 end-0 px-3 md:px-6 py-4 text-white">
                <div className="bg-gray-100/10 rounded-full px-2 py-1 w-fit flex items-center gap-2 mb-1">
                  <AnnouncementIconSvg className="w-4 h-4 text-white!" />
                  <p className="text-white text-[10px] md:text-xs font-medium">
                    {t("nav:modules.announcements")}
                  </p>
                </div>
                <h2 className="text-sm md:text-2xl font-semibold drop-shadow-md">
                  {announcement?.subject}
                </h2>
              </div>
            </div>

            {/* Scrollable Content Only */}
            <div className="flex-1 overflow-y-auto min-w-0">
              {isDataLoading ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-5 w-2/3 rounded" />
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-4/5 rounded" />
                  <Skeleton className="h-4 w-3/5 rounded" />
                </div>
              ) : (
                <div className="py-2 md:py-6 min-w-0 w-full">
                  {/* Rich Text Message */}
                  <div className="px-2 md:px-6 ql-container ql-snow border-none!">
                    <div
                      className="
                            text-xs md:text-sm text-black/90
                            leading-relaxed
                            w-full
                            min-w-0
                            break-keep
                            wrap-anywhere
                            **:break-keep
                            **:wrap-anywhere
                            [&_p]:mb-2
                            [&_strong]:font-semibold
                            [&_em]:italic
                            [&_u]:underline
                            [&_ol]:list-decimal [&_ol]:ms-6 [&_ol]:mb-2
                            [&_ul]:list-disc [&_ul]:ms-6 [&_ul]:mb-2
                            [&_li]:mb-1
                            [&_h1]:text-base [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:mt-3
                            [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:mt-3
                            [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-1 [&_h3]:mt-2
                            [&_a]:underline [&_a]:text-info [&_a]:cursor-pointer
                            [&_img]:max-w-full [&_img]:h-auto [&_img]:object-contain [&_img]:rounded-md [&_img]:my-2
                            [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-x-auto
                            [&_td]:border [&_td]:border-border [&_td]:p-2 [&_td]:text-xs
                            [&_th]:border [&_th]:border-border [&_th]:p-2 [&_th]:text-xs [&_th]:font-semibold [&_th]:bg-gray-50
                            [&_blockquote]:border-s-2 [&_blockquote]:border-gray-300 [&_blockquote]:ps-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-2
                        "
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          sanitizeRichText(announcement?.message || ""),
                        ),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Footer - Never scrolls */}
            {!isDataLoading && (
              <div className="shrink-0 border-t border-border bg-white px-2 md:px-6 py-3 flex flex-wrap justify-between items-center gap-2 text-xs text-gray-400">
                <p className="text-[10px] md:text-sm">
                  Published on:
                  <span className="ps-1 text-gray-500 text-[10px] md:text-sm">
                    {announcement?.triggeredAt
                      ? format(
                          new Date(announcement.triggeredAt),
                          "dd/MM/yyyy hh:mm aa",
                        )
                      : "-"}
                  </span>
                </p>
                <p className="flex gap-1 items-center text-gray-500 text-[10px] md:text-sm">
                  <Clock className="size-3 md:size-4" />
                  <span>
                    {announcement?.triggeredAt
                      ? formatNotificaitonTime(announcement.triggeredAt)
                      : "-"}
                  </span>
                </p>
              </div>
            )}
          </div>
        ),
      }}
    />
  );
};

export default AnnouncementModal;
